<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
import {
  NXGDSButton,
  NXGDSNavigationItem,
  NXGDSNavigationItemState,
  NXGDSTagIndicator,
  type NXGDSNavigationItemModel,
} from '@nsftx/seven-nxg-design-system'

interface PosNavbarProps {
  menuItems: NXGDSNavigationItemModel[]
  activeMenuItemIndex?: number
}

interface PosNavbarEmits {
  toggleMenuClick: []
  clickMenuItem: [
    menuItemIndex: number,
    menuItemId: NXGDSNavigationItemModel['uuid'],
    menuItemTitle: NXGDSNavigationItemModel['title'],
  ]
}

const { menuItems, activeMenuItemIndex = -1 } = defineProps<PosNavbarProps>()
const emit = defineEmits<PosNavbarEmits>()

const buttonBackgroundColor = 'var(--nxgds-mapped-color-surface-component-card-default)'

const navigationMenu = useTemplateRef('navigationMenu')
const menuItemsContainer = useTemplateRef('menuItemsContainer')
const isMenuItemsExpanded = ref(false)

const displayMenuItems = computed(() =>
  menuItems.map((menuItem, index) => ({
    ...menuItem,
    state:
      index === activeMenuItemIndex
        ? NXGDSNavigationItemState.active
        : NXGDSNavigationItemState.default,
  })),
)

const menuArrowIcon = computed(() => (isMenuItemsExpanded.value ? 'arrow-up-a' : 'arrow-down-a'))

const menuClasses = computed(() => ({
  'nxg-retail-pos-navbar__menu--expanded': isMenuItemsExpanded.value,
}))

const hasMenuItemsOverflow = computed(() =>
  menuItemsContainer.value ? hasWrappedMenuItems(menuItemsContainer.value) : false,
)

const showExpandMenuArrow = computed(() => hasMenuItemsOverflow.value)

const onWindowBlur = () => {
  if (!isMenuItemsExpanded.value || !(document.activeElement instanceof HTMLIFrameElement)) {
    return
  }

  hideExpandedMenuItems()
}

const onDocumentPointerDown = (event: PointerEvent) => {
  if (!isMenuItemsExpanded.value) {
    return
  }

  const menuElement = navigationMenu.value
  const target = event.target

  if (!(menuElement instanceof HTMLElement) || !(target instanceof Node)) {
    return
  }

  if (!menuElement.contains(target)) {
    hideExpandedMenuItems()
  }
}

onMounted(() => {
  window.addEventListener('blur', onWindowBlur)
  document.addEventListener('pointerdown', onDocumentPointerDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('blur', onWindowBlur)
  document.removeEventListener('pointerdown', onDocumentPointerDown)
})

const toggleExpandedMenuItems = () => {
  isMenuItemsExpanded.value = !isMenuItemsExpanded.value
}

const hideExpandedMenuItems = () => {
  isMenuItemsExpanded.value = false
}

const onToggleMenuClick = () => {
  emit('toggleMenuClick')
}

const onMenuItemClick = (menuItemIndex: number, menuItem: NXGDSNavigationItemModel) => {
  emit('clickMenuItem', menuItemIndex, menuItem.uuid, menuItem.title)
}

const hasWrappedMenuItems = (container: HTMLElement) => {
  const children = Array.from(container.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement,
  )

  if (children.length <= 1) {
    return false
  }

  const firstRowOffsetTop = children[0]?.offsetTop ?? 0

  return children.some(({ offsetTop }) => offsetTop > firstRowOffsetTop)
}
</script>

<template>
  <nav class="nxg-retail-pos-navbar" aria-label="Game navigation">
    <div class="nxg-retail-pos-navbar__toggle-menu">
      <NXGDSButton
        size="md"
        icon="menu-a"
        :background-color="buttonBackgroundColor"
        @click="onToggleMenuClick"
      />
    </div>
    <div
      ref="navigationMenu"
      class="nxg-retail-pos-navbar__menu"
      :class="menuClasses"
    >
      <div
        ref="menuItemsContainer"
        class="nxg-retail-pos-navbar__menu-items"
        data-testid="nxg-retail-pos-navbar-menu-items"
      >
        <NXGDSNavigationItem
          v-for="(menuItem, index) in displayMenuItems"
          :key="menuItem.uuid"
          :title="menuItem.title"
          :state="menuItem.state"
          :icon-name="menuItem.iconName"
          size="md"
          tabindex="0"
          @click="() => onMenuItemClick(index, menuItem)"
          @keydown.enter.prevent="() => onMenuItemClick(index, menuItem)"
          @keydown.space.prevent="() => onMenuItemClick(index, menuItem)"
        >
          <template v-if="menuItem.tags?.length" #tags>
            <NXGDSTagIndicator
              v-for="(tag, tagIndex) in menuItem.tags"
              :key="tagIndex"
              :label="tag.label"
              :variant="tag.variant"
            />
          </template>
        </NXGDSNavigationItem>
      </div>
      <NXGDSButton
        v-if="showExpandMenuArrow"
        size="md"
        :icon="menuArrowIcon"
        :background-color="buttonBackgroundColor"
        @click="toggleExpandedMenuItems"
      />
    </div>
  </nav>
</template>

<style scoped lang="scss">
.nxg-retail-pos-navbar {
  --nxg-retail-pos-navbar-height: 3.5rem;
  --nxg-retail-pos-navbar-toggle-width: 3.5rem;
  --nxg-retail-pos-navbar-menu-offset-left: calc(3.5rem + var(--nxgds-alias-spacing-3xs));
  --nxg-retail-pos-navbar-menu-shadow: 0 0.5rem 1rem 0 rgba(26, 28, 29, 0.75);
  --nxg-retail-pos-navbar-menu-expanded-max-height: 20rem;

  position: relative;
  display: flex;
  width: 100%;
  min-height: var(--nxg-retail-pos-navbar-height);
  gap: var(--nxgds-alias-spacing-3xs);
  border-radius: var(--nxgds-alias-radius-corner-sm);
  padding-bottom: var(--nxgds-alias-spacing-3xs);
  background-color: var(--nxgds-mapped-color-surface-component-page);

  &__toggle-menu {
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--nxg-retail-pos-navbar-height);
    height: var(--nxg-retail-pos-navbar-height);
    background-color: var(--nxgds-mapped-color-surface-component-card-default);
    border-radius: var(--nxgds-alias-radius-corner-sm);
    cursor: pointer;
  }

  &__menu {
    position: absolute;
    left: var(--nxg-retail-pos-navbar-menu-offset-left);
    right: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--nxgds-alias-spacing-xs);
    max-height: var(--nxg-retail-pos-navbar-height);
    padding: var(--nxgds-alias-spacing-xs);
    background-color: var(--nxgds-mapped-color-surface-component-card-default);
    border-radius: var(--nxgds-alias-radius-corner-sm);
    transition: all 0.1s ease-out;

    &--expanded {
      max-height: var(--nxg-retail-pos-navbar-menu-expanded-max-height);
      box-shadow: var(--nxg-retail-pos-navbar-menu-shadow);
    }
  }

  &__menu-items {
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    gap: var(--nxgds-alias-spacing-2xs);
    min-width: 0;
    overflow: hidden;
  }
}
</style>
